import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChatThreadList } from '../../components/chat/ChatThreadList';
import { ChatConversation } from '../../components/chat/ChatConversation';
import useChatStream from '../../hooks/useChatStream';
import { MessagesSquare, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'business', label: 'Business' },
  { key: 'host', label: 'My Party Chats' },
];

export const CrmCustomerMessages = () => {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/staff/threads?scope=${filter}`, { withCredentials: true });
      setThreads(res.data.threads || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { setLoading(true); fetchThreads(); }, [fetchThreads]);

  useChatStream(useCallback((evt) => {
    if (evt?.type === 'thread_message' || evt?.type === 'thread_read') fetchThreads();
  }, [fetchThreads]));

  return (
    <div data-testid="crm-customer-messages">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Customer Messages</h1>
        <p className="text-sm text-[#6B6B7A] mt-0.5">WhatsApp-style chats with customers — business inquiries and party-host threads.</p>
      </div>

      <div className="flex items-center gap-2 mb-3">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => { setFilter(f.key); setActive(null); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${filter === f.key ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'}`}
            data-testid={`crm-cmsg-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden shadow-sm grid grid-cols-1 md:grid-cols-[340px_1fr] h-[72vh]">
        <div className={`border-r border-[#E8E5DF] overflow-y-auto ${active ? 'hidden md:block' : 'block'}`}>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-[#6B6B7A]"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <ChatThreadList
              threads={threads}
              activeThreadId={active}
              onSelect={(t) => setActive(t.thread_id)}
              emptyText="No customer conversations yet."
            />
          )}
        </div>
        <div className={`${active ? 'block' : 'hidden md:block'} min-h-0`}>
          {active ? (
            <ChatConversation apiBase="staff" threadId={active} onBack={() => setActive(null)} onActivity={fetchThreads} />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center p-8 text-[#6B6B7A]">
              <MessagesSquare className="w-10 h-10 mb-3 text-[#E8E5DF]" />
              <p className="text-sm">Select a conversation to view and reply.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrmCustomerMessages;
